import { tExists } from '@aviv-estimation-funnel/shells/src/app/helpers/i18n';
import { ICustomValue, IFieldProps, IFieldValues } from '@mfe-types/field';
import { EErrorCode, IReactHookFormErrorContent } from '@mfe-types/validator';

const getErrorInterpolations = (error: IReactHookFormErrorContent) => {
    let interpolationParams = {} as Record<string, unknown>;

    if (error.type && error.types)
        switch (error.type) {
            case EErrorCode.Range:
                interpolationParams = {
                    min: error.types[0].minimum,
                    max: error.types[0].maximum,
                };
                break;
            case EErrorCode.MultipleOf:
                interpolationParams = {
                    multiple: error.types[0].multiple,
                };
                break;

            default:
                interpolationParams = {};
                break;
        }
    return interpolationParams;
};

interface IFieldTranslations {
    name: string;
    context: string;
    isRequired: boolean;
    isRequiredHidden: boolean | undefined;
    customValues?: ICustomValue[];
    translationParams?: Record<string, any>;
    t: (key: string, options?: Record<string, any>) => any;
}

export const getFieldTranslations = ({
    name,
    context,
    isRequired,
    isRequiredHidden,
    translationParams,
    customValues,
    t,
}: IFieldTranslations) => {
    const transContext = context ? `.${context.toLowerCase()}` : '';
    // we add a fallback to handle label that doesn't need context
    const label =
        tExists(t(`common.fields.${name}.label${transContext}`)) ??
        tExists(t(`common.fields.${name}.label`, { replace: translationParams }));

    return {
        label: isRequired && !isRequiredHidden && label ? `${label} *` : label,
        additionalText: tExists(
            t(`common.fields.${name}.additionalText`, { replace: translationParams }),
        ),
        placeholder:
            tExists(
                t(`common.fields.${name}.placeholder${transContext}`, {
                    replace: translationParams,
                }),
            ) ?? tExists(t(`common.fields.${name}.placeholder`, { replace: translationParams })),
        ariaLabel: tExists(
            t(`common.fields.${name}.ariaLabel`, {
                returnObjects: true,
                replace: translationParams,
            }),
        ) as any,
        suffix: tExists(t(`common.fields.${name}.suffix`)),
        tooltip: tExists(t(`common.fields.${name}.tooltip`)),
        helper: tExists(t(`common.fields.${name}.helper`)),
        ...(customValues && {
            customValues: customValues.map((item: ICustomValue) => ({
                ...item,
                label: tExists(t(`common.fields.${name}.${item.labelKey}`)),
            })),
        }),
    };
};

export const getOptionsTranslations = (
    name: string,
    t: (key: string, options?: Record<string, any>) => any,
    options?: IFieldProps<any, any>['options'],
) => {
    if (!options?.values) return null;

    return options.values.map((option: IFieldValues) => {
        let optionValue = undefined;
        if (typeof option?.value === 'string') {
            optionValue = option.value.charAt(0).toLowerCase() + option.value.slice(1);
        }
        if (option.translationKey) {
            optionValue = option.translationKey;
        }

        return {
            ...option,
            label: tExists(t(`common.fields.${name}.${optionValue}`)) ?? option.label,
            title: tExists(t(`common.fields.${name}.${optionValue}.label`)),
            additionalText: tExists(t(`common.fields.${name}.${optionValue}.additionalText`)),
        };
    });
};

export const getErrorTranslation = (
    name: string,
    t: (key: string, options?: Record<string, any>) => any,
    error?: IReactHookFormErrorContent,
) => {
    if (!error) return;

    const interpolation = getErrorInterpolations(error);

    return (
        tExists(t(`common.fields.${name}.error.${error.type}`, { replace: interpolation })) ??
        error.type
    );
};
